import React, { useState, useEffect } from 'react';
import { List, useNotify, useRefresh, useDataProvider } from 'react-admin';
import { Button, Typography, TextField } from '@mui/material';
import { baseUrl } from './consts';

const SlidersList = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [files, setFiles] = useState([]); // Holds objects with asset_name and URL
    const [isUploading, setIsUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState(null);
    const [link, setLink] = useState(''); // URL field

    const fetchSliderImages = () => {
        fetch(baseUrl+'/v1/manager/sliders', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.has_images) {
                    // Expecting server to return an array of objects: { asset_name, url }
                    setFiles(data.images); 
                } else {
                    setFiles([]);
                }
            })
            .catch(() => {
                notify('Failed to fetch slider images', { type: 'error' });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchSliderImages();
    }, [notify]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        const allowedExtensions = /(\.png|\.jpg|\.jpeg)$/i;
        if (!allowedExtensions.exec(selectedFile.name)) {
            notify('Invalid file format. Only PNG, JPG, and JPEG are allowed', { type: 'warning' });
            setSelectedFile(null);
            return;
        }
        setSelectedFile(selectedFile);
    };

    const handleUpload = () => {
        if (!selectedFile) {
            notify('Please select a file and enter a valid link', { type: 'warning' });
            return;
        }
        setIsUploading(true);

        dataProvider.create('slider', {
            data: {
                file: selectedFile,
                URL: link,
                asset_name: selectedFile.name, // Use the file name as the asset_name
            },
        })
            .then(() => {
                notify('Slider uploaded successfully', { type: 'info' });
                // Optimistically add the new file to the UI
                setFiles((prevFiles) => [
                    ...prevFiles,
                    {
                        asset_name: selectedFile.name,
                        url: URL.createObjectURL(selectedFile),
                    },
                ]);
                setSelectedFile(null);
                setLink('');
                setIsUploading(false);
            })
            .catch(() => {
                notify('Failed to upload slider', { type: 'error' });
                setIsUploading(false);
            });
    };

    const handleDelete = (assetName) => {
        dataProvider
            .delete('slider', { asset_name: assetName })
            .then(() => {
                notify('Slider deleted successfully', { type: 'info' });
                setFiles((prevFiles) => prevFiles.filter((file) => file.asset_name !== assetName));
            })
            .catch(() => {
                notify('Failed to delete slider', { type: 'error' });
            });
    };

    return (
        <List title="Sliders">
            {loading ? (
                <Typography variant="body1">Loading...</Typography>
            ) : files.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {files.map((file, index) => (
                        <div key={index} style={{ position: 'relative' }}>
                            <img
                                src={file.url}
                                alt={`Slider ${index + 1}`}
                                style={{
                                    width: '150px',
                                    height: '100px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    marginBottom: '8px',
                                }}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => handleDelete(file.asset_name)} // Pass asset_name for delete
                                style={{ position: 'absolute', top: 0, right: 0 }}
                            >
                                Delete
                            </Button>
                        </div>
                    ))}
                </div>
            ) : (
                <Typography variant="body1" style={{ marginBottom: '16px' }}>
                    No slider found.
                </Typography>
            )}

            <input
                type="file"
                accept=".png, .jpg, .jpeg"
                onChange={handleFileChange}
                style={{ marginTop: '16px', marginBottom: '8px' }}
            />
            <TextField
                label="Link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                fullWidth
                style={{ marginBottom: '16px' }}
            />
            <Button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                variant="contained"
                color="primary"
            >
                {isUploading ? 'Uploading...' : 'Upload Slider'}
            </Button>
        </List>
    );
};

export default SlidersList;
